import {
  useCallback, useEffect, useRef, useState,
} from 'react';
import { Nullable } from '../types';

// Flaga leży na origin frontu, nie w API — celowo nie idzie przez ApiService,
// bo ten ma baseURL wskazujący na osobną domenę backendu, która przy przerwie
// technicznej właśnie nie odpowiada.
export const MAINTENANCE_FLAG_URL = '/maintenance.json';

const IDLE_INTERVAL = 30000;
const ACTIVE_INTERVAL = 10000;

export type MaintenanceStatus = {
  isActive: boolean;
  message: Nullable<string>;
  until: Nullable<Date>;
  now: Date;
};

const inactiveStatus = (): MaintenanceStatus => ({
  isActive: false,
  message: null,
  until: null,
  now: new Date(),
});

const parseDate = (value: unknown): Nullable<Date> => {
  if (typeof value !== 'string') return null;

  const parsed = new Date(value);

  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const parseMessage = (value: unknown): Nullable<string> => {
  if (typeof value !== 'string' || !value.trim()) return null;

  return value.trim();
};

// Fail-open: brak pliku, timeout, zły JSON czy zły content-type oznaczają pracę
// normalną. Pomyłka w pliku na serwerze nie może nikogo zablokować.
const fetchStatus = async (): Promise<MaintenanceStatus> => {
  try {
    const response = await fetch(`${MAINTENANCE_FLAG_URL}?t=${Date.now()}`, { cache: 'no-store' });

    if (!response.ok) return inactiveStatus();

    // Przy nginxowym `try_files $uri /index.html` oraz na serwerze deweloperskim CRA
    // brakujący plik wraca jako index.html ze statusem 200, więc sam status nie
    // wystarcza do stwierdzenia, że flaga istnieje.
    const contentType = response.headers.get('content-type') || '';
    if (!contentType.includes('application/json')) return inactiveStatus();

    const data = await response.json();
    if (!data || typeof data !== 'object' || Array.isArray(data)) return inactiveStatus();

    // Flagą jest istnienie pliku, a `enabled` nie jest częścią formatu. Honorujemy je
    // wyłącznie po to, by intuicyjne `{"enabled": false}` wyłączyło tryb, zamiast go
    // włączyć — bez tego próba wyłączenia przerwy kładłaby aplikację wszystkim.
    // Właściwym sposobem wyłączenia pozostaje usunięcie pliku.
    if (data.enabled === false) return inactiveStatus();

    return {
      isActive: true,
      message: parseMessage(data.message),
      until: parseDate(data.until),
      // Zegar w przeglądarce potrafi być przesunięty o godziny, więc "teraz"
      // bierzemy z nagłówka Date odpowiedzi.
      now: parseDate(response.headers.get('date')) || new Date(),
    };
  } catch (error) {
    return inactiveStatus();
  }
};

const useMaintenance = (): MaintenanceStatus => {
  const [status, setStatus] = useState<MaintenanceStatus>(inactiveStatus);
  const wasActiveRef = useRef(false);

  const check = useCallback(async () => {
    const next = await fetchStatus();

    // Koniec przerwy: po deployu nazwy chunków się zmieniły, więc samo zdjęcie
    // bramki zostawiłoby aplikację ze starym kodem w pamięci.
    if (wasActiveRef.current && !next.isActive) {
      // `?maintenance=1` w adresie potrafi wymuszać ekran na stałe dla tej karty.
      // Zwykłe reload() przeładowałoby ten sam adres i natychmiast wymusiło ekran
      // od nowa, mimo że prawdziwa przerwa się skończyła — usuwamy więc ten
      // parametr z adresu, zanim strona się przeładuje.
      const url = new URL(window.location.href);
      url.searchParams.delete('maintenance');
      window.history.replaceState(null, '', url);
      window.location.reload();

      return;
    }

    wasActiveRef.current = next.isActive;
    setStatus(next);
  }, []);

  useEffect(() => {
    check();

    const interval = setInterval(check, status.isActive ? ACTIVE_INTERVAL : IDLE_INTERVAL);

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        check();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [check, status.isActive]);

  return status;
};

export default useMaintenance;
